/**
 * Replies Inbox — central view of every email reply across all leads.
 *
 * UX contract
 *   • Three filter pills at the top: Open · Handled · All — each shows a count.
 *   • Newest first, paginated 100 at a time.
 *   • Each row: company · contact · subject · 1-line preview · received_at.
 *   • Two row actions: "Mark handled" (toggles to "Mark open" if already handled)
 *     and "Open in CRM" (deep-links to /crm/leads/{lead_id}).
 *   • Optimistic UI on Mark-handled — the row immediately leaves the
 *     Open queue without waiting for the server to confirm.
 *
 * Data flow
 *   GET /api/crm/replies?status=open|handled|all — paginated
 *   POST /api/crm/replies/{event_id}/handle      — idempotent
 *   POST /api/crm/replies/{event_id}/unhandle    — idempotent
 *
 * Nothing here re-renders the full reply body — that already lives on
 * the lead's timeline at /crm/leads/{lead_id}, and the inbound mail
 * itself is already in sales@eyvo.com via the SendGrid Inbound Parse
 * forwarder. The inbox is a queue, not a mail client.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChatCircle, ArrowRight, CheckCircle, ArrowCounterClockwise, EnvelopeOpen,
  Spinner, PaperPlaneTilt, X, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const FILTERS = [
  { id: "open", label: "Open" },
  { id: "handled", label: "Handled" },
  { id: "all", label: "All" },
];

function formatTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function RepliesInboxPage() {
  const [filter, setFilter] = useState("open");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, handled: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Inline composer state — only one composer open at a time, keyed by
  // event_id. `composerText` is the textarea draft. We auto-prefill the
  // greeting line when opening, so the rep starts typing the actual
  // body on line 2.
  const [composerEventId, setComposerEventId] = useState(null);
  const [composerText, setComposerText] = useState("");
  const [composerSending, setComposerSending] = useState(false);
  const [dripWorkflows, setDripWorkflows] = useState([]);
  const [transferringId, setTransferringId] = useState(null);
  const navigate = useNavigate();

  // Pre-load the long-drip workflow list once on mount so the Transfer
  // button knows its target. Falls back gracefully — the button just
  // hides itself if no nurture campaigns are available.
  useEffect(() => {
    api.get("/crm/replies/drip-workflows")
      .then(res => setDripWorkflows(res.data || []))
      .catch(() => setDripWorkflows([]));
  }, []);

  const transferToWorkflow = async (evt, workflow) => {
    if (transferringId) return;
    if (!window.confirm(
      `Move ${evt.company_name || "this lead"} onto "${workflow.name}"?\n\n` +
      "This will pause every other active workflow enrolment for this " +
      "contact and mark the reply as handled.",
    )) return;
    setTransferringId(evt.event_id);
    try {
      const res = await api.post(
        `/crm/replies/${evt.event_id}/transfer-to-workflow`,
        { workflow_id: workflow.id, handle_reply: true },
      );
      const wf = res.data?.workflow_name || workflow.name;
      const alreadyEnrolled = res.data && res.data.enrolled === false;
      toast.success(
        alreadyEnrolled
          ? `${evt.company_name || "Lead"} was already on "${wf}" — reply marked handled.`
          : `Moved to "${wf}". Other workflows paused.`,
      );
      // Drop the row from Open view; counts will refresh on the next load.
      if (filter === "open") {
        setItems(prev => prev.filter(x => x.event_id !== evt.event_id));
        setCounts(c => ({ ...c, open: Math.max(0, c.open - 1), handled: c.handled + 1 }));
      } else {
        setItems(prev => prev.map(x => x.event_id === evt.event_id
          ? { ...x, handled_at: new Date().toISOString() }
          : x));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    }
    setTransferringId(null);
  };

  const load = useCallback(async (status = filter) => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/replies?status=${status}&limit=100`);
      setItems(res.data?.items || []);
      setCounts(res.data?.counts || { open: 0, handled: 0, total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load replies");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggleHandled = async (evt) => {
    if (busyId) return;
    setBusyId(evt.event_id);
    const isHandled = !!evt.handled_at;
    const url = `/crm/replies/${evt.event_id}/${isHandled ? "unhandle" : "handle"}`;
    try {
      await api.post(url, {});
      // Optimistic update — flip the row in place. Counts get re-fetched
      // on the next filter switch / refresh so we keep them eventually
      // consistent without a heavy round-trip.
      if (filter === "open" && !isHandled) {
        // Reply is leaving the open queue — drop it.
        setItems(prev => prev.filter(x => x.event_id !== evt.event_id));
        setCounts(c => ({ ...c, open: Math.max(0, c.open - 1), handled: c.handled + 1 }));
      } else if (filter === "handled" && isHandled) {
        // Reply unhandled — drop from this view.
        setItems(prev => prev.filter(x => x.event_id !== evt.event_id));
        setCounts(c => ({ ...c, handled: Math.max(0, c.handled - 1), open: c.open + 1 }));
      } else {
        // 'All' view — just flip the badge in place.
        setItems(prev => prev.map(x => x.event_id === evt.event_id
          ? { ...x, handled_at: isHandled ? null : new Date().toISOString() }
          : x));
        setCounts(c => isHandled
          ? { ...c, handled: Math.max(0, c.handled - 1), open: c.open + 1 }
          : { ...c, open: Math.max(0, c.open - 1), handled: c.handled + 1 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setBusyId(null); }
  };

  const openComposer = (evt) => {
    setComposerEventId(evt.event_id);
    // Auto-prefill greeting using the contact's first name (best effort —
    // strip everything after the first space if the name is multi-word).
    const firstName = (evt.contact_person || "").split(/\s+/)[0];
    setComposerText(firstName ? `Hi ${firstName},\n\n` : "Hi,\n\n");
  };

  const closeComposer = () => {
    setComposerEventId(null);
    setComposerText("");
  };

  const sendComposer = async (evt) => {
    const text = (composerText || "").trim();
    if (!text) {
      toast.error("Type something to send");
      return;
    }
    setComposerSending(true);
    try {
      // Convert plain-text to minimal HTML so paragraph breaks survive.
      const bodyHtml = text
        .split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("\n");
      const res = await api.post(`/crm/replies/${evt.event_id}/reply`, {
        body_html: bodyHtml,
        auto_handle: true,
      });
      toast.success(`Reply sent to ${res.data.to}`);
      closeComposer();
      // Auto-handle is on by default — the row leaves the Open queue.
      if (filter === "open") {
        setItems(prev => prev.filter(x => x.event_id !== evt.event_id));
        setCounts(c => ({ ...c, open: Math.max(0, c.open - 1), handled: c.handled + 1 }));
      } else if (filter === "all") {
        setItems(prev => prev.map(x => x.event_id === evt.event_id
          ? { ...x, handled_at: new Date().toISOString() }
          : x));
        setCounts(c => ({ ...c, open: Math.max(0, c.open - 1), handled: c.handled + 1 }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reply");
    } finally { setComposerSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="replies-inbox-page">
      <div className="flex items-center gap-3 mb-1">
        <ChatCircle size={20} weight="duotone" className="text-[#0055FF]" />
        <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Replies Inbox
        </h1>
      </div>
      <p className="text-sm text-[#525252] mb-6">
        Every email reply received from a lead, captured by the SendGrid Inbound Parse webhook. Mark replies as handled to clear them from the queue.
      </p>

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-4" data-testid="replies-filter-pills">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`inline-flex items-center gap-2 px-3 h-8 text-xs font-bold uppercase tracking-wider border ${
              filter === f.id
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-[#525252] border-[#E5E5E5] hover:bg-[#FAFAFA]"
            }`}
          >
            {f.label}
            <span className={`text-[10px] tabular-nums ${filter === f.id ? "text-white/70" : "text-[#A3A3A3]"}`}>
              {counts[f.id] ?? 0}
            </span>
          </button>
        ))}
        <button
          onClick={() => load()}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 h-8 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider hover:bg-[#FAFAFA] disabled:opacity-40"
          data-testid="refresh-replies-btn"
        >
          {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowCounterClockwise size={12} weight="bold" />} Refresh
        </button>
      </div>

      {/* Inbox table */}
      <div className="border border-[#E5E5E5] bg-white">
        {loading ? (
          <div className="px-5 py-12 text-center text-xs text-[#A3A3A3]" data-testid="replies-loading">
            <Spinner size={20} className="animate-spin mx-auto mb-2" />
            Loading replies…
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="replies-empty">
            <EnvelopeOpen size={36} weight="duotone" className="mx-auto text-[#A3A3A3] mb-3" />
            <p className="text-sm text-[#525252]">
              {filter === "open" ? "No open replies. Inbox zero." :
               filter === "handled" ? "Nothing handled yet." :
               "No replies have been captured yet."}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="replies-table">
            <thead className="bg-[#FAFAFA] text-[9px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Lead</th>
                <th className="text-left px-3 py-2 font-bold">Subject &amp; preview</th>
                <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Received</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-right px-4 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const handled = !!r.handled_at;
                const composerOpen = composerEventId === r.event_id;
                return (
                  <React.Fragment key={r.event_id}>
                  <tr className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`reply-row-${r.event_id}`}>
                    <td className="px-4 py-3 align-top">
                      <p className="font-semibold text-[#0A0A0A]">{r.company_name}</p>
                      {r.contact_person && (
                        <p className="text-[10px] text-[#A3A3A3] mt-0.5">{r.contact_person}</p>
                      )}
                      {r.contact_email && (
                        <p className="text-[10px] text-[#A3A3A3] truncate max-w-[200px]" title={r.contact_email}>{r.contact_email}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top max-w-[420px]">
                      <p className="font-semibold text-[#0A0A0A] truncate" title={r.subject}>{r.subject || "(no subject)"}</p>
                      {r.preview ? (
                        <p className="text-[#525252] mt-0.5 line-clamp-2 text-[11px]" data-testid={`reply-preview-${r.event_id}`}>{r.preview}</p>
                      ) : (
                        <p className="text-[10px] text-[#92400E] mt-0.5 italic"
                           data-testid={`reply-preview-empty-${r.event_id}`}>
                          ⚠ Body not captured (HTML-only reply pre-fix). Forward
                          to <code>sales@eyvo.com</code> or open in CRM →
                        </p>
                      )}
                      {r.source && (
                        <p className="text-[10px] text-[#A3A3A3] mt-1">via {r.source}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top text-[#525252] whitespace-nowrap">{formatTime(r.received_at)}</td>
                    <td className="px-3 py-3 align-top">
                      {handled ? (
                        <div>
                          <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#F0FDF4] text-[#16A34A]">Handled</span>
                          {r.handled_by && <p className="text-[10px] text-[#A3A3A3] mt-1">by {r.handled_by}</p>}
                        </div>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FFFBEB] text-[#D97706]">Open</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="flex flex-wrap items-center justify-end gap-1.5 max-w-[260px] ml-auto">
                        {!handled && (
                          <button
                            onClick={() => composerOpen ? closeComposer() : openComposer(r)}
                            disabled={!r.contact_email}
                            className={`inline-flex items-center gap-1 px-2.5 h-7 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 ${
                              composerOpen
                                ? "bg-[#0055FF] text-white border border-[#0055FF]"
                                : "border border-[#E5E5E5] hover:bg-white"
                            }`}
                            data-testid={`reply-toggle-${r.event_id}`}
                            title={r.contact_email ? "Send a quick reply to this lead" : "No email on file for this lead"}
                          >
                            <PaperPlaneTilt size={10} weight="bold" /> {composerOpen ? "Cancel" : "Reply"}
                          </button>
                        )}
                        <button
                          onClick={() => toggleHandled(r)}
                          disabled={busyId === r.event_id}
                          className="inline-flex items-center gap-1 px-2.5 h-7 border border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider hover:bg-white disabled:opacity-40"
                          data-testid={`toggle-handled-${r.event_id}`}
                          title={handled ? "Mark this reply as open again" : "Mark this reply as handled"}
                        >
                          {handled ? (
                            <><ArrowCounterClockwise size={10} weight="bold" /> Reopen</>
                          ) : (
                            <><CheckCircle size={10} weight="bold" /> Mark handled</>
                          )}
                        </button>
                        {!handled && dripWorkflows.length > 0 && dripWorkflows.map(wf => {
                          // Compact the workflow name for the button label.
                          // "Eyvo Long-term Nurture (post-drip)" → "Long-term Nurture"
                          const short = (wf.name || "")
                            .replace(/^Eyvo\s+/i, "")
                            .replace(/\s*\(.*?\)\s*/g, "")
                            .trim();
                          return (
                            <button
                              key={wf.id}
                              onClick={() => transferToWorkflow(r, wf)}
                              disabled={transferringId === r.event_id}
                              className="inline-flex items-center gap-1 px-2 h-7 border border-[#7C3AED] text-[#7C3AED] text-[10px] font-bold uppercase tracking-wider hover:bg-[#7C3AED] hover:text-white disabled:opacity-40 max-w-[180px]"
                              data-testid={`transfer-to-${wf.id}-${r.event_id}`}
                              title={`Move this lead onto "${wf.name}" — pauses every other workflow for this contact and marks the reply handled.`}
                            >
                              <ArrowsClockwise size={10} weight="bold" className="shrink-0" />
                              <span className="truncate">
                                {transferringId === r.event_id ? "Moving…" : `→ ${short}`}
                              </span>
                            </button>
                          );
                        })}
                        <button
                          onClick={() => navigate(`/crm/leads/${r.lead_id}`)}
                          className="inline-flex items-center gap-1 px-2.5 h-7 bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#262626]"
                          data-testid={`open-lead-${r.event_id}`}
                          title="Open the full lead record in CRM"
                        >
                          Open in CRM <ArrowRight size={10} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {composerOpen && (
                    <tr className="bg-[#FAFAFA] border-t border-[#E5E5E5]" data-testid={`composer-row-${r.event_id}`}>
                      <td colSpan={5} className="px-6 py-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#525252]">
                            Quick reply to {r.contact_person || r.contact_email}
                          </p>
                          <button
                            onClick={closeComposer}
                            className="p-1 text-[#A3A3A3] hover:text-[#525252]"
                            data-testid={`composer-close-${r.event_id}`}
                            title="Close composer"
                          >
                            <X size={14} weight="bold" />
                          </button>
                        </div>
                        <textarea
                          value={composerText}
                          onChange={(e) => setComposerText(e.target.value)}
                          rows={6}
                          autoFocus
                          placeholder="Write your reply…"
                          className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-xs leading-relaxed focus:outline-none focus:border-[#0055FF] resize-y"
                          data-testid={`composer-textarea-${r.event_id}`}
                        />
                        <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                          <p className="text-[10px] text-[#A3A3A3]">
                            Sends as <span className="font-semibold text-[#525252]">Eyvo</span> · Reply-To: lead's per-conversation address ·
                            Auto-marks this reply as handled.
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={closeComposer}
                              disabled={composerSending}
                              className="px-3 h-8 border border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider hover:bg-white"
                              data-testid={`composer-cancel-${r.event_id}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => sendComposer(r)}
                              disabled={composerSending || !composerText.trim()}
                              className="inline-flex items-center gap-1.5 px-3 h-8 bg-[#0055FF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-40"
                              data-testid={`composer-send-${r.event_id}`}
                            >
                              {composerSending ? (
                                <Spinner size={12} className="animate-spin" />
                              ) : (
                                <PaperPlaneTilt size={12} weight="bold" />
                              )}
                              {composerSending ? "Sending…" : "Send reply"}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
